import type { ViewDisplay, ViewDto, ViewLayout } from "@plannello/shared";
import type { ViewSearch } from "@/components/items/viewSearch";
import type { Catalog } from "@/data/catalog";

/**
 * The state of an item view, its URL form and its saved form, in one place.
 *
 *   URL (ViewSearch)  q, group, sub, sort, cols, subs, density, rows, zoom (+ view, peek)
 *   Saved (ViewDto)   query + layout + display { groupBy, subGroupBy, sort, columns, showSubItems,
 *                     density, rows, zoom, productKey }
 *
 * Opening a view writes its values into the URL (only those that differ from the defaults) next
 * to `?view=<id>`, so a reload or a shared link restores it without the view. The view is "dirty"
 * when the normalized URL state differs from the normalized saved state.
 */

export type ItemLayout = "list" | "board" | "timeline";

export const COLUMN_KEYS = [
  "priority",
  "key",
  "type",
  "status",
  "fields",
  "milestone",
  "start",
  "due",
  "estimate",
  "created",
  "updated",
  "assignee",
] as const;
export type ColumnKey = (typeof COLUMN_KEYS)[number];
export const DEFAULT_COLUMNS: ColumnKey[] = ["priority", "key", "type", "status", "fields", "updated", "assignee"];

export const DENSITIES = ["compact", "comfortable", "spacious"] as const;
export type ViewDensity = (typeof DENSITIES)[number];

export const TIMELINE_ROWS = ["epics", "all", "milestone"] as const;
export type TimelineRows = (typeof TIMELINE_ROWS)[number];
export const TIMELINE_ZOOMS = ["weeks", "months", "quarters"] as const;
export type TimelineZoom = (typeof TIMELINE_ZOOMS)[number];

/** Every display key with its default value (as a URL string). */
type Normalized = {
  q: string;
  group: string;
  sub: string;
  sort: string;
  cols: string;
  subs: string;
  density: string;
  rows: string;
  zoom: string;
};

const DEFAULTS: Normalized = {
  q: "",
  group: "status",
  sub: "none",
  sort: "rank",
  cols: DEFAULT_COLUMNS.join(","),
  subs: "on",
  density: "",
  rows: "epics",
  zoom: "months",
};

/** Keys that matter per layout (a board ignores columns, a timeline ignores grouping). */
const RELEVANT: Record<ItemLayout, (keyof Normalized)[]> = {
  list: ["q", "group", "sub", "sort", "cols", "subs", "density"],
  board: ["q", "group", "sort"],
  timeline: ["q", "rows", "zoom", "subs"],
};

export function parseColumns(raw: string | undefined): ColumnKey[] {
  if (!raw) return DEFAULT_COLUMNS;
  const keys = raw.split(",").filter((k): k is ColumnKey => (COLUMN_KEYS as readonly string[]).includes(k));
  return keys;
}

export function parseDensity(raw: string | undefined): ViewDensity | undefined {
  return (DENSITIES as readonly string[]).includes(raw ?? "") ? (raw as ViewDensity) : undefined;
}

export function parseTimelineRows(raw: string | undefined): TimelineRows {
  return (TIMELINE_ROWS as readonly string[]).includes(raw ?? "") ? (raw as TimelineRows) : "epics";
}

export function parseZoom(raw: string | undefined): TimelineZoom {
  return (TIMELINE_ZOOMS as readonly string[]).includes(raw ?? "") ? (raw as TimelineZoom) : "months";
}

export const layoutOf = (l: ViewLayout): ItemLayout => (l === "table" ? "list" : l);

function normalize(search: ViewSearch): Normalized {
  return {
    q: (search.q ?? "").trim(),
    group: search.group ?? DEFAULTS.group,
    sub: search.sub ?? DEFAULTS.sub,
    sort: search.sort ?? DEFAULTS.sort,
    cols: search.cols ?? DEFAULTS.cols,
    subs: search.subs === "off" ? "off" : "on",
    density: parseDensity(search.density) ?? "",
    rows: parseTimelineRows(search.rows),
    zoom: parseZoom(search.zoom),
  };
}

/** The URL search a saved view opens with (defaults left out, so URLs stay short). */
export function viewToSearch(view: Pick<ViewDto, "query" | "display">): ViewSearch {
  const d = (view.display ?? {}) as ViewDisplay & { rows?: string; zoom?: string };
  const full: Normalized = normalize({
    q: view.query || undefined,
    group: d.groupBy ?? undefined,
    sub: d.subGroupBy ?? undefined,
    sort: d.sort ?? undefined,
    cols: d.columns ? d.columns.join(",") || "none" : undefined,
    subs: d.showSubItems === false ? "off" : undefined,
    density: d.density ?? undefined,
    rows: d.rows,
    zoom: d.zoom,
  });
  return stripDefaults(full);
}

function stripDefaults(n: Normalized): ViewSearch {
  const out: ViewSearch = {};
  for (const k of Object.keys(n) as (keyof Normalized)[]) {
    if (n[k] !== DEFAULTS[k] && n[k] !== "") out[k] = n[k];
  }
  return out;
}

/** The display object to save for the current URL state. */
export function searchToDisplay(search: ViewSearch, productKey: string): ViewDisplay {
  const n = normalize(search);
  return {
    groupBy: n.group,
    subGroupBy: n.sub === "none" ? null : n.sub,
    sort: n.sort,
    columns: parseColumns(n.cols),
    showSubItems: n.subs !== "off",
    ...(n.density ? { density: n.density as ViewDensity } : {}),
    rows: n.rows,
    zoom: n.zoom,
    productKey,
  };
}

/** True when the URL state differs from the saved view (in the keys this layout uses). */
export function isDirty(view: ViewDto, layout: ItemLayout, search: ViewSearch): boolean {
  if (layoutOf(view.layout) !== layout) return true;
  const a = normalize(search);
  const b = normalize(viewToSearch(view));
  return RELEVANT[layout].some((k) => a[k] !== b[k]);
}

/** True when anything differs from a fresh, unsaved view of this layout. */
export function hasCustomState(layout: ItemLayout, search: ViewSearch): boolean {
  const a = normalize(search);
  return RELEVANT[layout].some((k) => a[k] !== DEFAULTS[k]);
}

/** The product a view belongs to (built-in id, shared scope or the key recorded at creation). */
export function viewProductKey(view: ViewDto, catalog: Catalog): string | null {
  if (view.builtin) return view.id.split(":")[1] ?? null;
  const recorded = (view.display as { productKey?: unknown } | undefined)?.productKey;
  if (typeof recorded === "string" && catalog.productByKey.has(recorded)) return recorded;
  if (view.scope === "product" && view.scopeId) return catalog.productById.get(view.scopeId)?.key ?? null;
  if (view.scope === "space" && view.scopeId) {
    const space = catalog.spaceById.get(view.scopeId);
    return space?.products.find((p) => p.isSpaceProduct)?.key ?? space?.products[0]?.key ?? null;
  }
  return null;
}

export type ViewLink = {
  to: "/p/$productKey/list" | "/p/$productKey/board" | "/p/$productKey/timeline" | "/p/$productKey/next-up";
  params: { productKey: string };
  search: ViewSearch;
};

/** Where a view opens. Built-in "Next up" is the ranked queue page. */
export function viewLink(view: ViewDto, productKey: string): ViewLink {
  const params = { productKey };
  if (view.queue || view.slug === "next-up") return { to: "/p/$productKey/next-up", params, search: {} };
  const layout = layoutOf(view.layout);
  return {
    to: `/p/$productKey/${layout}`,
    params,
    search: { ...viewToSearch(view), view: view.id },
  };
}

/** Does this view belong in the views menu of `productKey`? */
export function viewAppliesTo(view: ViewDto, productKey: string, catalog: Catalog): boolean {
  if (view.builtin) return view.id.split(":")[1] === productKey;
  const key = viewProductKey(view, catalog);
  if (view.scope === "space") return true;
  return key === null || key === productKey;
}
